import {Nullable, PriceType, StockInformationType} from "../../definitions/types";
import {currencyFormatter} from "../../utils/helpers";

/**
 * @class
 * @description Represents a price model for a product
 */
export class PriceModel {
    private readonly _price: PriceType;
    private readonly _minimumOrderQuantity: number;
    private readonly _isHardMinimumOrder: boolean;

    /**
     * @constructor
     * @description Creates a new instance of the model
     * @param price {Nullable<PriceType>}
     * @param stockInformation {Nullable<StockInformationType>}
     */
    constructor(public readonly price: PriceType, public readonly stockInformation: Nullable<StockInformationType>) {
        this._price = price;
        this._minimumOrderQuantity = stockInformation?.minQuantity ?? 1;
        this._isHardMinimumOrder = stockInformation?.isHardMinimumOrder ?? false;
    }

    /**
     * @method formatted
     * @description Returns the current price, unit price, sale amount, and sale percentage formatted as currency strings
     * @returns {{currentPrice: string, unitPrice: string, saleAmount: string | undefined, salePercentage: string | undefined}}
     * @example
     * {
     *    currentPrice: "$20.00",
     *    unitPrice: "$20.00",
     *    saleAmount: "$5.00",
     *    salePercentage: "(20%)"
     * }
     */
    public get formatted(): {
        startingPrice: string;
        currentPrice: string;
        unitPrice: string;
        saleAmount?: string;
        salePercentage?: number;
        width?: string;
        height?: string;
        unitOfMeasure: string;
    } {
        return {
            startingPrice: currencyFormatter.format(this.startingPrice),
            currentPrice: currencyFormatter.format(this.currentPrice),
            unitPrice: currencyFormatter.format(this.unitPrice),
            saleAmount: this._price.saleAmount ? currencyFormatter.format(this._price.saleAmount) : undefined,
            salePercentage: this._price.salePercentage ? Math.round(this._price.salePercentage) : undefined,
            width: this._price.width ? `${this._price.width}"` : undefined,
            height: this._price.height ? `${this._price.height}"` : undefined,
            unitOfMeasure: `unit${this._minimumOrderQuantity > 1 && `s`}`
        }
    }

    /**
     * @property startingPrice
     * @description Returns the starting price for the product in the current currency, taking into account the minimum order quantity
     * @returns {number}
     */
    public get startingPrice(): number {
        return this._price.price * this._minimumOrderQuantity;
    }

    /**
     * @method
     * @description Returns the current price
     * @returns {number | undefined}
     * @example
     * $20.00
     */
    public get currentPrice(): number {
        return this._price.price;
    }

    /**
     * @method
     * @description Returns the unit price in parentheses
     * @returns {number | undefined}
     * @example
     * $20.00
     */
    public get unitPrice(): number {
        return this._price.unitPrice;
    }

    /**
     * @method
     * @description Returns true if the product is on sale
     * @returns {boolean}
     * @example
     * true
     */
    public get onSale(): boolean {
        return this._price.onSale;
    }

    /**
     * @method
     * @description Returns the sale amount in parentheses
     * @returns {number | undefined}
     * @example
     * $20.00
     */
    public get saleAmount(): number | undefined {
        return this._price.saleAmount ? this._price.saleAmount : undefined;
    }

    /**
     * @method
     * @description Returns the sale percentage in parentheses
     * @returns {number | undefined}
     * @example
     * (20%)
     */
    public get salePercentage(): number | undefined {
        return this._price.salePercentage ? this._price.salePercentage : undefined;
    }

    /**
     * @method
     * @description Returns the low range of the price (if any)
     * @returns {number}
     * @example
     * 20
     */
    public get lowRange(): number {
        return this._price.lowRange;
    }

    /**
     * @method
     * @description Returns the high range of the price (if any)
     * @returns {number}
     * @example
     * 25
     */
    public get highRange(): number {
        return this._price.highRange;
    }

    /**
     * @method width
     * @description Returns the width of the product, in inches, if any
     * @returns {number | undefined}
     * @example
     * 20
     */
    public get width(): number | undefined {
        return this._price.width;
    }

    /**
     * @method height
     * @description Returns the height of the product, in inches, if any
     * @returns {number | undefined}
     * @example
     * 20
     */
    public get height(): number | undefined {
        return this._price.height;
    }

    /**
     * @property hasMinimumOrderQuantityRequirement
     * @description Returns true if the product has a minimum order quantity greater than 1 and is not a hard minimum order.
     * @returns {boolean}
     */
    public get hasMinimumOrderQuantityRequirement(): boolean {
        return this._minimumOrderQuantity > 1 && !this._isHardMinimumOrder;
    }

    /**
     * @property minimumOrderQuantity
     * @description Returns the minimum order quantity for the product
     * @returns {number}
     */
    public get minimumOrderQuantity(): number {
        return this._minimumOrderQuantity;
    }
}